import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { XCircle } from "lucide-react";
import api from "../../lib/api";
import {
  loadContratarPagoCtx,
  submitRedsysForm,
  type RedsysFormPayload,
} from "../../lib/redsysForm";
import { Logo } from "../../components/Logo";
import { PublicPageFrame } from "../../components/layout/PublicPageFrame";
import { PagoReintentoCard } from "../../components/pago/PagoReintentoCard";
import { AceptacionCondicionesPago } from "../../components/legal/AceptacionCondicionesPago";
import { useLegalComercio } from "../../context/LegalComercioContext";
import type { LegalVendedorInfo } from "../../content/legalComercio";

const cardClass =
  "bg-white/[0.92] backdrop-blur-[8px] rounded-2xl shadow-2xl p-8 w-full border border-white/20 max-w-md text-center";

export function ContratarErrorPage() {
  const [searchParams] = useSearchParams();
  const { setLegalVendedor } = useLegalComercio();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aceptaCondiciones, setAceptaCondiciones] = useState(false);

  const ctx = useMemo(() => {
    const seguroParam = searchParams.get("seguro");
    const tokenParam = searchParams.get("token");
    const seguroFromUrl = seguroParam ? parseInt(seguroParam, 10) : NaN;
    const stored = loadContratarPagoCtx();

    const seguroId =
      !Number.isNaN(seguroFromUrl) && seguroFromUrl > 0
        ? seguroFromUrl
        : stored?.seguroId ?? NaN;
    const token = tokenParam?.trim() || stored?.token || "";
    const pilotoId = stored?.pilotoId;

    return {
      seguroId,
      pilotoId,
      token,
      puedeReintentar:
        !Number.isNaN(seguroId) && seguroId > 0 && token.length > 0,
    };
  }, [searchParams]);

  useEffect(() => {
    const token = ctx.token.trim();
    if (!token) {
      setLegalVendedor(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ vendedor?: LegalVendedorInfo }>(
          `/public/contratar/${encodeURIComponent(token)}`
        );
        if (!cancelled) {
          setLegalVendedor(res.data.vendedor ?? null);
        }
      } catch {
        if (!cancelled) {
          setLegalVendedor(null);
        }
      }
    })();

    return () => {
      cancelled = true;
      setLegalVendedor(null);
    };
  }, [ctx.token, setLegalVendedor]);

  const handleRetry = async () => {
    if (!ctx.puedeReintentar) return;
    if (!aceptaCondiciones) {
      setError("Debes aceptar las condiciones de compra para reintentar el pago.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await api.post<{ formData: RedsysFormPayload }>(
        `/public/contratar/${encodeURIComponent(ctx.token)}/reintentar-pago`,
        {
          seguro_id: ctx.seguroId,
          ...(ctx.pilotoId != null ? { piloto_id: ctx.pilotoId } : {}),
        }
      );
      if (res.data.formData?.actionUrl) {
        submitRedsysForm(res.data.formData);
        return;
      }
      setError("No se pudo preparar el pago. Inténtalo de nuevo.");
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          "Error al reintentar el pago. Inténtalo de nuevo."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicPageFrame>
      <div className={cardClass}>
        <Logo className="mx-auto mb-6 h-10 scale-[2] object-contain" />
        <XCircle className="mx-auto mb-4 h-16 w-16 text-red-600" aria-hidden />
        {ctx.puedeReintentar ? (
          <div className="text-left">
            <PagoReintentoCard
              description="Tus datos ya están registrados. Solo tienes que volver a introducir la tarjeta en el banco."
              onRetry={handleRetry}
              loading={loading}
              error={error}
              retryDisabled={!aceptaCondiciones}
            >
              <AceptacionCondicionesPago
                checked={aceptaCondiciones}
                onChange={setAceptaCondiciones}
                id="acepta-condiciones-contratar-retry"
                className="mt-4"
              />
            </PagoReintentoCard>
          </div>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-[#2C2C2C]">
              El pago no se ha completado
            </h1>
            <p className="mt-4 text-sm text-gray-700">
              Si tienes el enlace del circuito, ábrelo de nuevo y usa el botón de pago desde la
              misma página de contratación.
            </p>
          </>
        )}
      </div>
    </PublicPageFrame>
  );
}
